/**
 * Glyphs drawn by `DrawnGlyph`: Lucide icons split into strokes, ported from the temp worker
 * app (jobbiz_app_v2, src/components/ui/drawnGlyphs.js). For each one: its strokes in drawing
 * order (`d`, length, window in the 0..1 cycle), the dots that fade in instead of being drawn,
 * and the optional gesture that ends the drawing (twist, shake, drop, hop, drive, toss).
 *
 * A closed outline is cut into two halves starting from the same point, drawn together. Lengths
 * are measured on the paths and rounded up to cover the round caps.
 */
export const GLYPHS = {
    // Inbox from the bottom, its slot, then it takes in (nothing).
    inbox: {
        strokes: [
            {
                d: "M12 20H4a2 2 0 0 1-2-2v-6l3.45-6.89A2 2 0 0 1 7.24 4H12",
                length: 33,
                draw: [0.02, 0.26],
            },
            {
                d: "M12 20h8a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H12",
                length: 33,
                draw: [0.02, 0.26],
            },
            {
                d: "M2 12h6l2 3h4l2-3h6",
                length: 24.5,
                draw: [0.26, 0.4],
            },
        ],
        gesture: {
            kind: "drop",
            at: [0.42, 0.6],
        },
    },
    // Verification rosette from its top, then the check, and it hops.
    badgeCheck: {
        strokes: [
            {
                d: "M12 2.005A4 4 0 0 1 15.37 3.85A4 4 0 0 1 20.15 8.63A4 4 0 0 1 20.15 15.37A4 4 0 0 1 15.38 20.15A4 4 0 0 1 12.005 22.003",
                length: 33,
                draw: [0.02, 0.3],
            },
            {
                d: "M12 2.005A4 4 0 0 0 8.63 3.85A4 4 0 0 0 3.85 8.62A4 4 0 0 0 3.85 15.38A4 4 0 0 0 8.63 20.15A4 4 0 0 0 12.005 22.003",
                length: 33,
                draw: [0.02, 0.3],
            },
            {
                d: "M8 12L10.5 14.5L16 9",
                length: 12.5,
                draw: [0.33, 0.43],
            },
        ],
        gesture: {
            kind: "hop",
            at: [0.46, 0.6],
        },
    },
    // Magnifier, its handle, the cross, and it shakes its head.
    searchX: {
        strokes: [
            {
                d: "M16.657 16.657A8 8 0 0 1 5.343 5.343",
                length: 26.5,
                draw: [0.02, 0.22],
            },
            {
                d: "M16.657 16.657A8 8 0 0 0 5.343 5.343",
                length: 26.5,
                draw: [0.02, 0.22],
            },
            {
                d: "M16.7 16.7l4.3 4.3",
                length: 7,
                draw: [0.21, 0.28],
            },
            {
                d: "M8.5 8.5l5 5",
                length: 8,
                draw: [0.31, 0.37],
            },
            {
                d: "M13.5 8.5l-5 5",
                length: 8,
                draw: [0.37, 0.43],
            },
        ],
        gesture: {
            kind: "shake",
            at: [0.45, 0.62],
        },
    },
    // Page from the bottom, the folded corner, the written lines, and it hops.
    fileText: {
        strokes: [
            {
                d: "M12 22H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8",
                length: 37.5,
                draw: [0.02, 0.24],
            },
            {
                d: "M12 22h6a2 2 0 0 0 2-2V8a2.4 2.4 0 0 0-.706-1.704l-3.588-3.588A2.4 2.4 0 0 0 14 2",
                length: 31.5,
                draw: [0.02, 0.24],
            },
            {
                d: "M14 2v5a1 1 0 0 0 1 1h5",
                length: 12.5,
                draw: [0.23, 0.31],
            },
            {
                d: "M8 9h2",
                length: 3,
                draw: [0.32, 0.35],
            },
            {
                d: "M8 13h8",
                length: 9,
                draw: [0.35, 0.41],
            },
            {
                d: "M8 17h8",
                length: 9,
                draw: [0.41, 0.47],
            },
        ],
        gesture: {
            kind: "hop",
            at: [0.49, 0.6],
        },
    },
    // Both pen edges from the tip, the barrel, the slit, the eyelet, then a flourish.
    penTool: {
        strokes: [
            {
                d: "M2.293 2.293A1 1 0 0 1 3.235 2.028L15.879 5.35A1 1 0 0 1 16.625 6.126L18 13",
                length: 23,
                draw: [0.02, 0.22],
            },
            {
                d: "M2.293 2.293A1 1 0 0 0 2.028 3.235L5.35 15.879A1 1 0 0 0 6.126 16.625L13 18",
                length: 23,
                draw: [0.02, 0.22],
            },
            {
                d: "M19 12.414A1 1 0 0 0 18.293 12.707L12.707 18.293A1 1 0 0 0 12.707 19.707L14.293 21.293A1 1 0 0 0 15 21.586",
                length: 14.5,
                draw: [0.21, 0.32],
            },
            {
                d: "M19 12.414A1 1 0 0 1 19.707 12.707L21.293 14.293A1 1 0 0 1 21.293 15.707L15.707 21.293A1 1 0 0 1 15 21.586",
                length: 14.5,
                draw: [0.21, 0.32],
            },
            {
                d: "M2.3 2.3L9.586 9.586",
                length: 11.5,
                draw: [0.33, 0.41],
            },
            {
                d: "M9.586 9.586A2 2 0 0 1 12.414 12.414",
                length: 7,
                draw: [0.4, 0.47],
            },
            {
                d: "M9.586 9.586A2 2 0 0 0 12.414 12.414",
                length: 7,
                draw: [0.4, 0.47],
            },
        ],
        gesture: {
            kind: "twist",
            at: [0.49, 0.66],
        },
    },
    // Receipt from the middle of its serrated edge, its lines, and it drops in.
    receiptText: {
        strokes: [
            {
                d: "M12 3.005A1.3 1.3 0 0 0 12.7 2.8L13.633 2.2A1.3 1.3 0 0 1 15.033 2.2L15.967 2.8A1.3 1.3 0 0 0 17.367 2.8L18.3 2.2A1.3 1.3 0 0 1 19 2A1 1 0 0 1 20 3V21A1 1 0 0 1 19 22A1.3 1.3 0 0 1 18.3 21.8L17.367 21.2A1.3 1.3 0 0 0 15.967 21.2L15.033 21.8A1.3 1.3 0 0 1 13.633 21.8L12.7 21.2A1.3 1.3 0 0 0 12 20.995",
                length: 37.5,
                draw: [0.02, 0.3],
            },
            {
                d: "M12 3.005A1.3 1.3 0 0 1 11.3 2.8L10.367 2.2A1.3 1.3 0 0 0 8.967 2.2L8.033 2.8A1.3 1.3 0 0 1 6.633 2.8L5.7 2.2A1.3 1.3 0 0 0 5 2A1 1 0 0 0 4 3V21A1 1 0 0 0 5 22A1.3 1.3 0 0 0 5.7 21.8L6.633 21.2A1.3 1.3 0 0 1 8.033 21.2L8.967 21.8A1.3 1.3 0 0 0 10.367 21.8L11.3 21.2A1.3 1.3 0 0 1 12 20.995",
                length: 37.5,
                draw: [0.02, 0.3],
            },
            {
                d: "M8 8h6",
                length: 7,
                draw: [0.31, 0.36],
            },
            {
                d: "M8 12h8",
                length: 9,
                draw: [0.36, 0.42],
            },
            {
                d: "M8 16h5",
                length: 6,
                draw: [0.42, 0.47],
            },
        ],
        gesture: {
            kind: "drop",
            at: [0.49, 0.66],
        },
    },
    // Top, both bulbs from top and bottom to the neck, bottom, and it gets flipped.
    hourglass: {
        strokes: [
            {
                d: "M5 2h14",
                length: 15,
                draw: [0.02, 0.1],
            },
            {
                d: "M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2",
                length: 25,
                draw: [0.1, 0.32],
            },
            {
                d: "M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22",
                length: 25,
                draw: [0.1, 0.32],
            },
            {
                d: "M5 22h14",
                length: 15,
                draw: [0.32, 0.4],
            },
        ],
        gesture: {
            kind: "twist",
            at: [0.44, 0.64],
        },
    },
    // Calendar from the top, header, rings, the cross, and it shakes its head.
    calendarX: {
        strokes: [
            {
                d: "M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7",
                length: 35.5,
                draw: [0.02, 0.24],
            },
            {
                d: "M12 3h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-7",
                length: 35.5,
                draw: [0.02, 0.24],
            },
            {
                d: "M3 9h18",
                length: 19,
                draw: [0.24, 0.32],
            },
            {
                d: "M8 2v3",
                length: 4,
                draw: [0.32, 0.37],
            },
            {
                d: "M16 2v3",
                length: 4,
                draw: [0.32, 0.37],
            },
            {
                d: "M10 13l4 4",
                length: 7,
                draw: [0.38, 0.43],
            },
            {
                d: "M14 13l-4 4",
                length: 7,
                draw: [0.43, 0.48],
            },
        ],
        gesture: {
            kind: "shake",
            at: [0.5, 0.67],
        },
    },
    // The star from its top point, clockwise in one stroke, and it hops.
    star: {
        strokes: [
            {
                d: "M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z",
                length: 69,
                draw: [0.02, 0.4],
            },
        ],
        gesture: {
            kind: "hop",
            at: [0.44, 0.56],
        },
    },
    // Lucide square-check: the box from its top centre, both halves together, then the tick, and it hops.
    squareCheck: {
        strokes: [
            { d: "M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7", length: 35, draw: [0.02, 0.26] },
            { d: "M12 3h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-7", length: 35, draw: [0.02, 0.26] },
            { d: "m9 12 2 2 4-4", length: 9.5, draw: [0.3, 0.4] },
        ],
        gesture: { kind: "hop", at: [0.44, 0.56] },
    },
};
